import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faHeart, faShareAlt, faEnvelope, faCopy, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import useTokenStore from '../../store/store';
import useThemeStore from '../../store/themeStore';
import { ThemeProps } from '../../utils/theme';
import axios from 'axios';
import { baseUrl } from '../../utils';
import { useNavigate } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { numberFormat } from '../../utils/properties';
import { useFavoritesStore } from '../../store/favoritesStore';

interface FavoritesModalProps {
  isOpen: boolean;
  onClose: () => void;
  themeColor: string;
  textColor: string;
}

interface FavoriteProperty {
  _id: string;
  propertyOf: string;
  price: number;
  images: Array<{ url: string }>;
  bedrooms: number;
  region: string;
  strategy: string;
  tenureType: string;
  occupancy: string;
  discountToMarket: number;
  rentGrossYield: number;
  estimatedValue: number;
  investment: number;
  averageRent: number;
  reserved: boolean;
}

const FavoritesModal: React.FC<FavoritesModalProps> = ({ isOpen, onClose, themeColor, textColor }) => {
  const [shareModalProperty, setShareModalProperty] = useState<FavoriteProperty | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [animateIcons, setAnimateIcons] = useState(false);
  const getToken = useTokenStore((state) => state.token);
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const navigate = useNavigate();
  const { favorites, isLoading, fetchFavorites, removeFavorite } = useFavoritesStore();

  useEffect(() => {
    if (isOpen && getToken) {
      fetchFavorites();
      // Lock scrolling on the body while modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scrolling when modal closes
      document.body.style.overflow = '';
    }
    
    // Cleanup function to ensure scrolling is restored if component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, getToken, fetchFavorites]);

  // Animation effect for share icons
  useEffect(() => {
    if (isShareModalOpen) {
      setAnimateIcons(true);
      setTimeout(() => setAnimateIcons(false), 800); // duration of pulse animation
    }
  }, [isShareModalOpen]);

  const removeFavoriteHandler = async (propertyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!getToken) return;
    
    try {
      // Optimistically update UI for better user experience
      removeFavorite(propertyId);
      
      // Make the API call
      await axios.post(
        `${baseUrl}/whitelabel/toggleFavoriteProperty`,
        { propertyId },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${(getToken as string).replace("B9I9N9456", "")}`
          },
        }
      );
    } catch (error) {
      console.error('Error removing favorite:', error);
      // If an error occurs, refresh the favorites list
      fetchFavorites();
    }
  };

  const navigateToProperty = (propertyId: string) => {
    navigate(`/listing/${propertyId}`);
    onClose();
  };

  const handleShareClick = (e: React.MouseEvent, property: FavoriteProperty) => {
    e.stopPropagation();
    setShareModalProperty(property);
    setIsShareModalOpen(true);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setShareModalProperty(null);
  };

  const formatAddress = (address: string) => {
    return address?.trim().replace(/,\s*$/, '') || '';
  };

  // Utility function to check for white or near-white shades
  const getBackgroundAndTextColor = (color: string, fallbackColor: string, defaultTextColor: string) => {
    if (isWhiteOrShade(color)) {
      return {
        backgroundColor: fallbackColor,
        textColor: 'white', // Set the text color to white if the background is near white
      };
    } else {
      return {
        backgroundColor: color,
        textColor: defaultTextColor, // Keep the original text color
      };
    }
  };
  
  const isWhiteOrShade = (color: string) => {
    if (!color) return false;
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
  
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
  
    return false;
  };

  // Share modal functions
  const generatePropertyLink = (propertyId: string) => {
    const baseUrl = window.location.origin; 
    return `${baseUrl}/listing/${propertyId}`;
  };

  const [copyFeedback, setCopyFeedback] = useState('Copy Link');

  const handleCopyLink = () => {
    if (!shareModalProperty) return;
    const link = generatePropertyLink(shareModalProperty._id);
    navigator.clipboard.writeText(link);
    setCopyFeedback('Link Copied');
    setTimeout(() => {
      setCopyFeedback('Copy Link');
      closeShareModal();
    }, 2000);
  };

  const handleWhatsAppShare = () => {
    if (!shareModalProperty) return;
    const link = generatePropertyLink(shareModalProperty._id);
    window.open(`https://wa.me/?text=${encodeURIComponent(link)}`, '_blank');
    closeShareModal();
  };

  const handleEmailShare = () => {
    if (!shareModalProperty) return;
    const link = generatePropertyLink(shareModalProperty._id);
    window.location.href = `mailto:?subject=Check out this property&body=${encodeURIComponent(link)}`;
    closeShareModal();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        transition: 'all 0.3s ease',
        opacity: isOpen ? 1 : 0,
        visibility: isOpen ? 'visible' : 'hidden'
      }}
      onClick={onClose}
    >
      <div 
        className="relative bg-white rounded-lg shadow-xl w-[95%] md:w-4/5 max-w-5xl max-h-[90vh] md:max-h-[80vh] flex flex-col" 
        style={{ 
          backgroundColor: 'white',
          transform: isOpen ? 'scale(1)' : 'scale(0.95)',
          opacity: isOpen ? 1 : 0,
          transition: 'transform 0.3s ease, opacity 0.3s ease'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b" style={{ backgroundColor: themeColor, color: isWhiteOrShade(themeColor) ? 'black' : textColor }}>
          <h2 className="text-xl md:text-2xl font-bold flex items-center">
            <FontAwesomeIcon icon={faHeart} className="mr-2" />
            My Favourite Properties
          </h2>
          <button 
            onClick={onClose}
            className="text-xl md:text-2xl hover:scale-110 transition duration-200"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 md:p-6">
          {isLoading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: themeColor }}></div>
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-8 md:py-12">
              <p className="text-base md:text-lg text-gray-600">You haven't added any properties to your favourites yet.</p>
              <button 
                onClick={() => {
                  navigate('/listing');
                  onClose();
                }}
                className="mt-4 px-4 md:px-6 py-2 rounded-full hover:scale-105 transition duration-200"
                style={{ 
                  backgroundColor: themeColor, 
                  color: isWhiteOrShade(themeColor) ? 'black' : textColor 
                }}
              >
                Browse Properties
              </button>
            </div>
          ) : (
            <div className="w-full">
              {/* Mobile view - hide table header */}
              <div className="md:hidden">
                {favorites.map((property) => (
                  <div 
                    key={property._id}
                    className="border border-gray-300 rounded-lg shadow-md hover:shadow-xl transition duration-150 ease-in-out mb-4 cursor-pointer"
                    onClick={() => navigateToProperty(property._id)}
                  >
                    <div className="bg-gray-100 p-2 rounded-t-lg">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          {property.reserved && (
                            <span className="text-xs font-bold text-white py-1 px-2 rounded-md mr-2"
                              style={{
                                background: `${getThemes?.color2}`,
                                color: isWhiteOrShade(getThemes?.color1) ? "white" : getThemes?.txtColor,
                              }}>
                              Reserved
                            </span>
                          )}
                          <span className="font-bold text-sm">
                            {property.rentGrossYield}% Gross Yield
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon 
                            icon={faShareAlt} 
                            onClick={(e) => handleShareClick(e, property)} 
                            className="cursor-pointer" 
                            style={{ transition: 'transform 0.2s', fontSize: '1rem' }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          />
                          <FontAwesomeIcon 
                            icon={faTrash} 
                            onClick={(e) => removeFavoriteHandler(property._id, e)} 
                            className="cursor-pointer text-red-500" 
                            style={{ transition: 'transform 0.2s', fontSize: '1rem' }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="flex items-start mb-3">
                        <LazyLoadImage
                          src={property.images && property.images[0] ? property.images[0].url : ""}
                          width={96}
                          height={96}
                          className="w-24 h-20 object-cover rounded mr-3"
                          alt={property.propertyOf}
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-sm mb-1 line-clamp-2">
                            {formatAddress(property.propertyOf)}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {property.tenureType && (
                              <span className="text-xs font-bold text-white py-0.5 px-1.5 rounded-md"
                              style={{
                                background: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).backgroundColor,
                                color: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).textColor,
                              }}>
                                {property.tenureType}
                              </span>
                            )}
                            {property.occupancy && property.occupancy.toLowerCase() === 'tenanted' && (
                              <span className="text-xs font-bold text-white py-0.5 px-1.5 rounded-md"
                              style={{
                                background: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).backgroundColor,
                                color: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).textColor,
                              }}>
                                Tenanted
                              </span>
                            )}
                            {property.discountToMarket > 0 && (
                              <span 
                                className="text-xs font-bold text-white py-0.5 px-1.5 rounded-md"
                                style={{
                                  background: `${getThemes?.color1}`,
                                  color: `${getThemes?.txtColor}`,
                                }}
                              >
                                {property.discountToMarket.toFixed(1)}% Discount
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-600">Price</p>
                          <p className="font-semibold">£{numberFormat(property.price)}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Valuation</p>
                          <p className="font-semibold">£{numberFormat(property.estimatedValue)}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Investment</p>
                          <p className="font-semibold">£{numberFormat(property.investment)}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Rental Income</p>
                          <p className="font-semibold">£{numberFormat(property.averageRent)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop view - table layout */}
              <div className="hidden md:block">
                <table className="min-w-full bg-white">
                  <thead className="rounded-lg overflow-hidden">
                    <tr className="bg-gray-100">
                      <th className="py-2 px-9 text-left font-semibold w-2/5">Property</th>
                      <th className="py-2 pr-9 text-right font-semibold w-[15%]">Price</th>
                      <th className="py-2 pr-9 text-right font-semibold w-[15%]">Valuation</th>
                      <th className="py-2 pr-9 text-right font-semibold w-[15%]">Investment</th>
                      <th className="py-2 px-4 text-right font-semibold w-[15%]">Rental Income</th>
                    </tr>
                  </thead>
                  <tbody className='mt-4'>
                    {favorites.map((property) => (
                      <tr key={property._id} className="bg-white mb-4">
                        <td colSpan={6} className="p-0">
                          <div 
                            className="border border-gray-300 rounded-lg shadow-md hover:shadow-xl transition duration-150 ease-in-out mb-4 cursor-pointer"
                            onClick={() => navigateToProperty(property._id)}
                          >
                            <div className="bg-gray-100 p-2 rounded-t-lg">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                  {property.reserved && (
                                    <span className="text-xs font-bold text-white py-1 px-2 rounded-md mr-2"
                                      style={{
                                        background: `${getThemes?.color2}`,
                                        color: isWhiteOrShade(getThemes?.color1) ? "white" : getThemes?.txtColor,
                                      }}>
                                      Reserved
                                    </span>
                                  )}
                                  <span className="font-bold">
                                    {property.rentGrossYield}% Gross Yield
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <FontAwesomeIcon 
                                    icon={faShareAlt} 
                                    onClick={(e) => handleShareClick(e, property)} 
                                    className="cursor-pointer" 
                                    style={{ transition: 'transform 0.2s', fontSize: '1.1rem' }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                  />
                                  <FontAwesomeIcon 
                                    icon={faTrash} 
                                    onClick={(e) => removeFavoriteHandler(property._id, e)} 
                                    className="cursor-pointer text-red-500" 
                                    style={{ transition: 'transform 0.2s', fontSize: '1.1rem' }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                  />
                                </div>
                              </div>
                            </div>
                            <table className="w-full">
                              <tbody>
                                <tr>
                                  <td className="py-4 px-4 w-2/5">
                                    <div className="flex items-center">
                                      <LazyLoadImage
                                        src={property.images && property.images[0] ? property.images[0].url : ""}
                                        width={96}
                                        height={96}
                                        className="w-36 h-24 object-cover rounded mr-4"
                                        alt={property.propertyOf}
                                      />
                                      <div className="flex flex-col justify-center">
                                        <p 
                                          className="font-semibold text-sm mb-1 address"
                                          style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                          }}
                                        >
                                          {formatAddress(property.propertyOf)}
                                        </p>
                                        <div className="flex flex-wrap gap-1 mb-1">
                                          {property.tenureType && (
                                            <span className="text-xs font-bold text-white py-0.5 px-1.5 rounded-md"
                                            style={{
                                              background: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).backgroundColor,
                                              color: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).textColor,
                                            }}>
                                              {property.tenureType}
                                            </span>
                                          )}
                                          {property.occupancy && property.occupancy.toLowerCase() === 'tenanted' && (
                                            <span className="text-xs font-bold text-white py-0.5 px-1.5 rounded-md"
                                            style={{
                                              background: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).backgroundColor,
                                              color: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).textColor,
                                            }}>
                                              Tenanted
                                            </span>
                                          )}
                                          {property.discountToMarket > 0 && (
                                            <span 
                                              className="text-xs font-bold text-white py-0.5 px-1.5 rounded-md"
                                              style={{
                                                background: `${getThemes?.color1}`,
                                                color: `${getThemes?.txtColor}`,
                                              }}
                                            >
                                              {property.discountToMarket.toFixed(1)}% Discount
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-4 pr-15 text-right font-semibold w-[15%]">£{numberFormat(property.price)}</td>
                                  <td className="py-4 pr-15 text-right font-semibold w-[15%]">£{numberFormat(property.estimatedValue)}</td>
                                  <td className="py-4 pr-5 text-right font-semibold w-[15%]">£{numberFormat(property.investment)}</td>
                                  <td className="py-4 px-4 text-right font-semibold w-[15%]">£{numberFormat(property.averageRent)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Share Modal */}
      {isShareModalOpen && shareModalProperty && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-[99999]" 
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.7)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            closeShareModal();
          }}
        >
          <div className="bg-white rounded-lg p-4 md:p-6 w-[90%] md:w-96 relative" style={{ backgroundColor: 'white' }}>
            <style>
              {`
                @keyframes pulse {
                  0%, 100% { transform: scale(1); }
                  30% { transform: scale(1.2); }
                }
                .pulse {
                  animation: pulse 1s infinite;
                }
              `}
            </style>
            <span 
              onClick={closeShareModal}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                cursor: "pointer",
                fontSize: "20px",
                fontWeight: "bold",
                transition: "transform 0.2s",
                display: "inline-block",
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              &times;
            </span>
            <h3 className="text-xl md:text-2xl font-bold mb-4 text-center">Share Property</h3>
            <div className="flex items-center mb-4">
              {shareModalProperty.images && shareModalProperty.images.length > 0 && (
                <LazyLoadImage
                  src={shareModalProperty.images[0].url}
                  width={120}
                  height={60}
                  className="rounded mr-4 object-cover"
                  alt="Property Thumbnail"
                  style={{ width: '100px', height: '60px', objectFit: 'cover' }}
                />
              )}
              <div className="flex-1">
                <h4 className="font-medium text-sm md:text-base">{shareModalProperty.propertyOf}</h4>
              </div>
            </div>
            <p className="mb-4 text-center text-sm md:text-base">
              You can share the following link for anyone to view this property
            </p>
            <div className="flex justify-around">
              <div className="flex flex-col items-center">
                <button
                  className={`focus:outline-none transition duration-300 ease-in-out transform hover:scale-110 ${animateIcons ? 'pulse' : ''}`}
                  onClick={handleCopyLink}
                >
                  <FontAwesomeIcon icon={faCopy} size="1x" className="mb-2" style={{ fontSize: '1.5rem' }} />
                </button>
                <span style={{ fontSize: '0.7rem' }}>{copyFeedback}</span>
              </div>

              <div className="flex flex-col items-center">
                <button
                  className={`focus:outline-none transition duration-300 ease-in-out transform hover:scale-110 ${animateIcons ? 'pulse' : ''}`}
                  onClick={handleWhatsAppShare}
                  style={{ 
                    width: '2rem', 
                    height: '2rem', 
                    paddingTop: "2px",
                    backgroundColor: '#25D366', 
                    borderRadius: '60%', 
                    justifyContent: 'center', 
                    alignItems: 'center' 
                  }}
                >
                  <FontAwesomeIcon icon={faWhatsapp} style={{ fontSize: '1.3rem', color: 'white' }} />
                </button>
                <span style={{ fontSize: '0.7rem' }}>Whatsapp</span>
              </div>

              <div className="flex flex-col items-center">
                <button
                  className={`focus:outline-none transition duration-300 ease-in-out transform hover:scale-110 ${animateIcons ? 'pulse' : ''}`}
                  onClick={handleEmailShare}
                >
                  <FontAwesomeIcon icon={faEnvelope} size="1x" className="mb-2" style={{ fontSize: '1.5rem' }} />
                </button>
                <span style={{ fontSize: '0.7rem' }}>Email</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default FavoritesModal;